// Google Analytics 4 E-commerce Tracking

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Types for e-commerce events
export interface GAProduct {
  item_id: string;
  item_name: string;
  price: number;
  quantity?: number;
  item_category?: string;
  item_brand?: string;
  item_variant?: string;
}

export interface GAEcommerceEvent {
  currency?: string;
  value?: number;
  items: GAProduct[];
  transaction_id?: string;
  shipping?: number;
  tax?: number;
}

// Initialize GA4
export const GA_TRACKING_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Page view tracking
export const pageview = (url: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
    });
  }
};

// Custom event tracking
export const event = (
  action: string,
  category?: string,
  label?: string,
  value?: number
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// E-commerce: View item
export const viewItem = (product: GAProduct) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'PLN',
      value: product.price,
      items: [product],
    });
  }
};

// E-commerce: Add to cart
export const addToCart = (product: GAProduct, quantity: number = 1) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: 'PLN',
      value: product.price * quantity,
      items: [
        {
          ...product,
          quantity,
        },
      ],
    });
  }
};

// E-commerce: Remove from cart
export const removeFromCart = (product: GAProduct, quantity: number = 1) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'remove_from_cart', {
      currency: 'PLN',
      value: product.price * quantity,
      items: [
        {
          ...product,
          quantity,
        },
      ],
    });
  }
};

// E-commerce: View cart
export const viewCart = (items: GAProduct[], value: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'view_cart', {
      currency: 'PLN',
      value,
      items,
    });
  }
};

// E-commerce: Begin checkout
export const beginCheckout = (items: GAProduct[], value: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: 'PLN',
      value,
      items,
    });
  }
};

// E-commerce: Add shipping info
export const addShippingInfo = (items: GAProduct[], value: number, shippingTier?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'add_shipping_info', {
      currency: 'PLN',
      value,
      shipping_tier: shippingTier,
      items,
    });
  }
};

// E-commerce: Add payment info
export const addPaymentInfo = (items: GAProduct[], value: number, paymentType?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'add_payment_info', {
      currency: 'PLN',
      value,
      payment_type: paymentType,
      items,
    });
  }
};

// E-commerce: Purchase
export const purchase = (data: GAEcommerceEvent) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'purchase', {
      currency: data.currency || 'PLN',
      transaction_id: data.transaction_id,
      value: data.value,
      shipping: data.shipping || 0,
      tax: data.tax || 0,
      items: data.items,
    });
  }
};

// E-commerce: Refund
export const refund = (transactionId: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'refund', {
      currency: 'PLN',
      transaction_id: transactionId,
      value,
    });
  }
};

// User engagement: Search
export const search = (searchTerm: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'search', {
      search_term: searchTerm,
    });
  }
};

// User engagement: Select content
export const selectContent = (contentType: string, itemId: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'select_content', {
      content_type: contentType,
      item_id: itemId,
    });
  }
};

// User engagement: Share
export const share = (method: string, contentType: string, itemId: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'share', {
      method,
      content_type: contentType,
      item_id: itemId,
    });
  }
};

// Conversion tracking helper
export const trackConversion = (conversionLabel: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${GA_TRACKING_ID}/${conversionLabel}`,
      value: value,
      currency: 'PLN',
    });
  }
};

